import React, { useState } from 'react';
import CombinedEditableSection from '../../../../../../components/CombinedEditableSection.comp';
import { parseInlineMarkdown } from '../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../utils/dq_lc';
import {
  SlideWithImage,
  SlideImageContainer,
  ImagePlaceholder,
  SlidePointsContainer,
  SlidePoint,
  SlideTitle,
  PointText,
} from '../../../../../styles/LearnTab/slideBase.styles';
import { SlideTitleHeader } from '../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../common/EditableSlideField.comp';
import { SlideImageLightboxModal } from '../common/SlideImageLightbox.comp';

export const GraphicAndTextSlide = ({
  slide,
  highlightWord,
  isEditMode = false,
  slideEdits = {},
  showEnglishContent = false,
  onSaveEdit,
  onResetEdit,
  onCancelEdit,
  onImageClick,
  uiLocale = 'en-US',
}) => {
  const [internalLightbox, setInternalLightbox] = useState(null);
  const origin = slide?._pbdq_origin || slide || {};
  const isBigIdea = slide?._pbdq_kind === 'big_idea';
  const isCaseStudy = slide?._pbdq_kind === 'case_study' || origin.slide_type === 'case_study';
  const isWhy = slide?._pbdq_kind === 'why' || origin.slide_type === 'why' || origin.positive_outcome || origin.if_you_do_this_well || origin.negative_outcome || origin.if_you_skip_this;
  const isIllustrative = origin.is_illustrative;

  const posImg = origin.positive_image_url || origin.positive_image || slide?.positive_image_url || null;
  const negImg = origin.negative_image_url || origin.negative_image || slide?.negative_image_url || null;
  const hasOutcomeImages = Boolean(posImg || negImg);

  const handleImgClick = (img) => {
    if (onImageClick) {
      onImageClick(img);
    } else {
      setInternalLightbox(img);
    }
  };

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  return (
    <>
      <SlideWithImage>
        {slide?.Images && slide.Images.length > 0 && (!isWhy || !hasOutcomeImages) && (
          <SlideImageContainer>
            <ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                onClick={() => handleImgClick(slide.Images[0])}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  WebkitUserDrag: 'none',
                }}
              />
            </ImagePlaceholder>
          </SlideImageContainer>
        )}
        <SlidePointsContainer style={{ width: '100%' }}>
          <SlidePoint>
            {isEditMode ? (
              <CombinedEditableSection
                slide={slide}
                isEditMode={isEditMode}
                slideEdits={slideEdits}
                showEnglishContent={showEnglishContent}
                onSave={onSaveEdit}
                onReset={onResetEdit}
                onCancel={onCancelEdit}
                TitleComponent={SlideTitle}
                ContentComponent={PointText}
              />
            ) : (
              <>
                {isCaseStudy ? (() => {
                  const rawTitle = origin.case_title || slide?.SlideTitle || origin.title || origin.headline || '';
                  const titleEdit = slideEdits['title'];
                  const baseTitle = showEnglishContent ? (titleEdit?.original || rawTitle) : (titleEdit?.edited || rawTitle);
                  if (!baseTitle) return null;
                  const displayTitle = baseTitle;
                  return (
                    <SlideTitle>
                      {highlightWord ? highlightWord('title', displayTitle) : displayTitle}
                    </SlideTitle>
                  );
                })() : (
                  <SlideTitleHeader
                    slide={slide}
                    slideEdits={slideEdits}
                    showEnglishContent={showEnglishContent}
                    highlightWord={highlightWord}
                  />
                )}

                {isCaseStudy && isIllustrative && (
                  <div className="illustrative-badge" style={{ marginBottom: 12 }}>
                    {tUi('slide.content.illustrativeExample', uiLocale)}
                  </div>
                )}

                {(() => {
                  if (isCaseStudy) {
                    return (
                      <div className="content-body" style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.6, textAlign: 'left', width: '100%' }}>
                        {parseInlineMarkdown(origin.case_snippet || slide?.SlideContent)}
                      </div>
                    );
                  }
                  if (isWhy) {
                    const doWell = origin.positive_outcome || origin.if_you_do_this_well || '';
                    const skipThis = origin.negative_outcome || origin.if_you_skip_this || '';
                    const posLabel = origin.positive_label || tUi('slide.outcome.ifYouDoThisWell', uiLocale);
                    const negLabel = origin.negative_label || tUi('slide.outcome.ifYouSkipThis', uiLocale);
                    const posIcon = (origin.positive_icon || '').trim();
                    const negIcon = (origin.negative_icon || '').trim();
                    const takeaway = origin.takeaway || '';
                    const bodyText = origin.body_text || origin.message || '';

                    if (doWell || skipThis) {
                      return (
                        <div className={`outcome-container ${hasOutcomeImages ? 'centered-container' : ''}`}>
                          <div className={`outcome-grid ${hasOutcomeImages ? 'centered-grid' : ''}`}>
                            {doWell && (
                              <div className="outcome-card positive">
                                {posImg && (
                                  <div className="outcome-card-image-wrap">
                                    <img
                                      src={posImg}
                                      alt="Positive outcome"
                                      onClick={() => handleImgClick(posImg)}
                                      className="outcome-card-image"
                                    />
                                  </div>
                                )}
                                <div className="outcome-header positive">
                                  {posIcon && <span className="outcome-icon positive">{posIcon}</span>}
                                  <span className="outcome-title positive">{posLabel}</span>
                                </div>
                                <div className="outcome-body positive">
                                  {parseInlineMarkdown(doWell)}
                                </div>
                              </div>
                            )}
                            {skipThis && (
                              <div className="outcome-card negative">
                                {negImg && (
                                  <div className="outcome-card-image-wrap">
                                    <img
                                      src={negImg}
                                      alt="Negative outcome"
                                      onClick={() => handleImgClick(negImg)}
                                      className="outcome-card-image"
                                    />
                                  </div>
                                )}
                                <div className="outcome-header negative">
                                  {negIcon && <span className="outcome-icon negative">{negIcon}</span>}
                                  <span className="outcome-title negative">{negLabel}</span>
                                </div>
                                <div className="outcome-body negative">
                                  {parseInlineMarkdown(skipThis)}
                                </div>
                              </div>
                            )}
                          </div>
                          {takeaway && (
                            <div className="content-so-what">
                              {parseInlineMarkdown(takeaway)}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return <PointText>{parseInlineMarkdown(bodyText || slide?.SlideContent)}</PointText>;
                  }
                  return renderTextWithId('content_0', slide?.SlideContent, PointText, isBigIdea ? { className: 'big-idea-text' } : {}, textRendererOptions);
                })()}
              </>
            )}
          </SlidePoint>
        </SlidePointsContainer>
      </SlideWithImage>

      {!onImageClick && (
        <SlideImageLightboxModal
          image={internalLightbox}
          onClose={() => setInternalLightbox(null)}
        />
      )}
    </>
  );
};

export default GraphicAndTextSlide;
